import React, { FC, KeyboardEvent, RefObject, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { CheckOutlined } from '@mui/icons-material'
import {
  Box,
  ClickAwayListener,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  PopperProps,
  SxProps,
} from '@mui/material'
import { Theme } from '@mui/system'

import { usePrevious } from '@hooks'
import {
  DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL,
  GENERATOR_INPUT_TYPE,
  UPDATE_TYPE,
} from '@constants'

const updateTypes = [UPDATE_TYPE.WEBSOCKET, UPDATE_TYPE.INTERVAL, UPDATE_TYPE.MANUAL]

type PropsType = {
  anchorRef: RefObject<HTMLButtonElement>
  isUpdateSettingsOpen: boolean
  onToggleUpdateSettings: (value: boolean) => void
  updateType: UPDATE_TYPE
  onSwitchUpdateType: (newUpdateType: UPDATE_TYPE, newUpdateInterval: number) => void
  updateInterval: number
  position?: PopperProps['placement']
  sx?: SxProps<Theme>
  isActiveTimeWindow?: boolean
}

export const UpdateSettingsPopup: FC<PropsType> = ({
  anchorRef,
  isUpdateSettingsOpen,
  onToggleUpdateSettings,
  updateType,
  onSwitchUpdateType,
  updateInterval,
  position = 'bottom-end',
  sx,
  isActiveTimeWindow,
}) => {
  const { t } = useTranslation()
  const methods = useForm<{ updateInterval: number; updateType: UPDATE_TYPE }>({
    defaultValues: { updateInterval, updateType },
  })

  const { watch, setValue, setError, clearErrors, reset } = methods
  const [previousUpdateType, setPreviousUpdateType] = useState<UPDATE_TYPE | undefined>()
  const [previousUpdateInterval] = usePrevious(updateInterval)

  const watchUpdateInterval = watch('updateInterval')
  const watchUpdateType = watch('updateType')

  useEffect(() => {
    if (watchUpdateType === updateType) return

    setValue('updateType', updateType)
  }, [updateType])

  useEffect(() => {
    if (updateInterval === previousUpdateInterval) return

    setValue('updateInterval', updateInterval)
  }, [updateInterval])

  useEffect(() => {
    if (isActiveTimeWindow) {
      if (watchUpdateType !== UPDATE_TYPE.MANUAL && watchUpdateType !== UPDATE_TYPE.WEBSOCKET) {
        setPreviousUpdateType(watchUpdateType)
        setValue('updateType', UPDATE_TYPE.WEBSOCKET)
        onSwitchUpdateType(UPDATE_TYPE.WEBSOCKET, watchUpdateInterval)
      }
    } else if (previousUpdateType && !isActiveTimeWindow) {
      setValue('updateType', previousUpdateType)
      onSwitchUpdateType(previousUpdateType, watchUpdateInterval)
      setPreviousUpdateType(undefined)
    }
  }, [isActiveTimeWindow])

  const onPopperClose = (type: UPDATE_TYPE = watchUpdateType) => {
    if (
      type === UPDATE_TYPE.INTERVAL &&
      (!watchUpdateInterval ||
        !Number.isFinite(Number(watchUpdateInterval)) ||
        Number(watchUpdateInterval) <= 0)
    ) {
      setError('updateInterval', { type: 'valueAsNumber' })

      return
    }
    const updateInterval =
      Number.isFinite(Number(watchUpdateInterval)) && Number(watchUpdateInterval) > 0
        ? Number(watchUpdateInterval)
        : DEFAULT_TIME_IN_SECONDS_FOR_UPDATE_INTERVAL
    onSwitchUpdateType(type, updateInterval)
    setValue('updateInterval', updateInterval)
    onToggleUpdateSettings(false)
    clearErrors()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      onPopperClose()
    }
    if (
      e.key === 'e' ||
      e.key === 'E' ||
      e.key === '-' ||
      e.key === '+' ||
      e.key === '.' ||
      e.key === ','
    ) {
      e.preventDefault()
    }
  }

  const handleItemClick = (type: UPDATE_TYPE) => {
    setValue('updateType', type)
    if (type !== UPDATE_TYPE.INTERVAL) {
      onPopperClose(type)
    }
  }

  return (
    <Popper
      anchorEl={anchorRef?.current}
      open={isUpdateSettingsOpen}
      placement={position}
      sx={{
        zIndex: theme => theme.zIndex.tooltip,
      }}
    >
      <ClickAwayListener mouseEvent={'onMouseDown'} onClickAway={() => onPopperClose()}>
        <Box
          sx={{
            mt: '4px',
            mr: '-4px',
            backgroundColor: theme => theme.palette.background.paper,
            boxShadow: theme => theme.palette.boxShadow.paper,
            border: theme => theme.palette.border.field,
            ...sx,
          }}
        >
          <FormProvider {...methods}>
            {updateTypes.map(type => (
              <ListItemButton
                key={type}
                selected={watchUpdateType === type}
                disabled={
                  isActiveTimeWindow &&
                  type !== UPDATE_TYPE.MANUAL &&
                  type !== UPDATE_TYPE.WEBSOCKET
                }
                onClick={() => {
                  handleItemClick(type)
                }}
              >
                <ListItemIcon sx={{ width: 36, minWidth: 0 }}>
                  {watchUpdateType === type && <CheckOutlined />}
                </ListItemIcon>
                <ListItemText primary={t(`updateSettings.${type.toLowerCase()}`)} />
                {type === UPDATE_TYPE.INTERVAL && (
                  <FormInput
                    formInputContainerSx={{ flex: 1 }}
                    formLabelSx={{ minWidth: '100px' }}
                    inputType={GENERATOR_INPUT_TYPE.INPUT}
                    maxLengthInput={5}
                    name='updateInterval'
                    replacePattern={'^[0-9]'}
                    size='small'
                    type={'number'}
                    value={watchUpdateInterval}
                    textFieldInputProps={{
                      inputProps: { min: 1 },
                    }}
                    onKeyDown={handleKeyDown}
                  />
                )}
              </ListItemButton>
            ))}
          </FormProvider>
        </Box>
      </ClickAwayListener>
    </Popper>
  )
}
